import { Component, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorInterceptor } from 'src/app/common/services/http-error-interceptor';
import { Router, ActivatedRoute } from '@angular/router';
import { AdvertiserEditComponent } from '../advertiser-edit/advertiser-edit.component';
import { AdvertiserDto, AdvertiserQueryRequestDto, CampaignsAdvertiserAdminApiClient, IAdvertiserQueryRequestDto, SwaggerException } from 'src/app/api/api-campaigns.service.generated';
import { PERMISSION } from 'src/app/const/permission';
import { ColumnData, NgxDataTableComponent } from 'wecheer-common';
import { lastValueFrom } from 'rxjs';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { UserPermissionsService } from 'src/app/common/services/user-permissions.service';


@Component({
  selector: 'app-advertiser-grid',
  templateUrl: './advertiser-grid.component.html',
  styleUrls: ['./advertiser-grid.component.scss'],
  imports: [
    NgxDataTableComponent,
    MatPaginatorModule,
  ]
})
export class AdvertiserGridComponent implements OnInit {
  private advertiserApi = inject(CampaignsAdvertiserAdminApiClient);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private userPermissionsService = inject(UserPermissionsService);

  pageSize = 25;
  readonly permissions = PERMISSION;

  rows: AdvertiserDto[] = [];
  columns: ColumnData[] = [
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
    {
      key: 'actionButtons',
      label: 'Actions',
      sortable: false,
      colWidth: 100,
      actionButtons: [
        { icon: 'visibility', tooltip: 'View', handler: (advertiser: AdvertiserDto) => this.onView(advertiser) },
        { icon: 'edit', tooltip: 'Edit', visible: this.checkPermissions([PERMISSION.CAMPAIGNMANAGEMENT_WRITE, PERMISSION.CAMPAIGN_UPDATE]), handler: (advertiser: AdvertiserDto) => this.onEdit(advertiser) }
      ]
    }
  ];

  filter: IAdvertiserQueryRequestDto = {};
  currentPage = 0;
  totalCount = 0;
  isLoading = false;
  isError = false;
  isErrorRetry = false;
  error = '';

  ngOnInit() {
    this.loadPage(0);
  }

  async loadPage(page: number) {
    this.isLoading = true;
    this.isError = false;
    this.error = '';

    const query = new AdvertiserQueryRequestDto({
      ...this.filter,
      skip: page * this.pageSize,
      maxItems: this.pageSize,
    });

    try {
      const chunk = await HttpErrorInterceptor.runWithDisable4xxIntercept(
        async () => await lastValueFrom(this.advertiserApi.queryAdvertisers(query)));
      if (!chunk) return;

      this.rows = chunk.items ?? [];
      this.totalCount = chunk.total ?? 0;
      this.currentPage = page;
    } catch (error: any) {
      const err: SwaggerException = error;
      this.isError = true;
      this.isErrorRetry = true;
      this.error = JSON.stringify(err);
    } finally {
      this.isLoading = false;
    }
  }

  onPageChange(event: PageEvent) {
    if (event.pageSize !== this.pageSize) {
      this.pageSize = event.pageSize;
      this.loadPage(0);
    } else {
      this.loadPage(event.pageIndex);
    }
  }

  async search(f: IAdvertiserQueryRequestDto) {
    if (!f)
      f = this.filter;
    this.filter = f;
    this.loadPage(0);
  }

  async onView(advertiser: AdvertiserDto) {
    this.router.navigate([advertiser.id], { relativeTo: this.route });
  }

  async onEdit(advertiser: AdvertiserDto) {
    const config = {
      ...AdvertiserEditComponent.config,
      data: advertiser
    };
    const edited = await lastValueFrom(this.dialog.open(AdvertiserEditComponent, config).afterClosed());
    if (!edited)
      return;

    this.loadPage(this.currentPage);
  }

  onActivate(_e: any) {
  }

  get total() { return this.totalCount; }

  getRowClass(_row: any) {
    return {};
  }

  checkPermissions(permissions: string[]): boolean {
    let hasPermission = false;
    this.userPermissionsService.hasAnyPermission(permissions).subscribe(value => {
      hasPermission = value;
    });
    return hasPermission;
  }
}


